/**
 * Domain Purchase — Admin Type Definitions
 *
 * Mirrors backend DTOs for the domain registrations and TLD catalog
 * admin endpoints. Enum string values use PascalCase to match the
 * backend C# enums exactly.
 */

// ============================================================================
// Domain Registration (read-only for P0 admin)
// ============================================================================

/**
 * Domain registration lifecycle status.
 * Mirrors backend DomainStatus enum (design §4.1).
 */
export const DOMAIN_STATUSES = [
  "PendingRegistration",
  "Registering",
  "Active",
  "RegistrationFailed",
  "Expiring",
  "Expired",
  "RedemptionPeriod",
  "Suspended",
  "TransferPending",
  "TransferFailed",
  "Cancelled",
] as const;

export type DomainStatus = (typeof DOMAIN_STATUSES)[number];

/**
 * Domain registration list item — one row in the admin operations list.
 * Mirrors backend DomainRegistrationAdminDto.
 */
export interface DomainRegistrationAdminDto {
  /** Domain registration ID (UUID) */
  id: string;

  /** Fully-qualified domain name, e.g. "example.com" */
  fqdn: string;

  /** Registration lifecycle status */
  status: DomainStatus;

  /** Owning tenant ID (UUID) */
  tenantId: string;

  /** Owning tenant display name */
  tenantName: string;

  /** Owning user ID (UUID) */
  userId: string;

  /** TLD including the dot, e.g. ".com" */
  tld: string;

  /** Registrar provider that owns this registration, e.g. "ResellerClub" */
  registrarProvider: string;

  /** Registrar's domain/order reference, null when never registered */
  registrarReference: string | null;

  /** Order item ID that purchased this domain (UUID, null if none) */
  orderItemId: string | null;

  /** Registration term length in years */
  registrationYears: number;

  /** Registration timestamp (ISO 8601, null until registered) */
  registeredAt: string | null;

  /** Expiry timestamp (ISO 8601, null until registered) */
  expiresAt: string | null;

  /** Whether auto-renew is enabled */
  autoRenew: boolean;

  /** Failure reason, present when status is RegistrationFailed/TransferFailed */
  failureReason: string | null;

  /** Creation timestamp (ISO 8601) */
  createdAt: string;
}

/**
 * Filter parameters for the domain registrations admin list.
 */
export interface DomainRegistrationFilters {
  /** Status filter; "All" means no status filter is applied */
  status?: DomainStatus | "All";

  /** Page number (1-based) */
  page: number;

  /** Items per page */
  pageSize: number;
}

// ============================================================================
// TLD Catalog (full CRUD for P0 admin)
// ============================================================================

/**
 * TLD catalog entry — a sellable top-level domain with retail pricing.
 * Mirrors backend TldCatalogDto (design §6.2).
 *
 * Money fields hold the amount in the currency's smallest unit as returned by
 * the backend. Display code must format according to the currency's decimal
 * places (e.g. use {@link formatCurrency} from `@/lib/currency`).
 */
export interface TldCatalogDto {
  /** TLD catalog entry ID (UUID) */
  id: string;

  /** The TLD itself, e.g. ".com", ".io", ".dev" */
  tld: string;

  /** UI display label */
  displayName: string;

  /** Registrar provider serving this TLD, e.g. "ResellerClub" */
  registrarProvider: string;

  /** Last synced wholesale cost (smallest currency unit) */
  registrarCost: number;

  /** Retail registration price per year (smallest currency unit) */
  registrationPrice: number;

  /** Retail renewal price per year (smallest currency unit) */
  renewalPrice: number;

  /** Retail transfer-in price (smallest currency unit) */
  transferPrice: number;

  /** Retail redemption restore price (smallest currency unit) */
  restorePrice: number;

  /** Catalog currency code, e.g. "USD", "VND" */
  currency: string;

  /** Minimum allowed registration term in years */
  minRegistrationYears: number;

  /** Maximum allowed registration term in years */
  maxRegistrationYears: number;

  /** Whether WHOIS privacy is offered for this TLD */
  supportsWhoisPrivacy: boolean;

  /** Per-year WHOIS privacy add-on price (smallest currency unit; 0 if free) */
  whoisPrivacyPrice: number;

  /** Whether full DNS hosting is available */
  supportsDnsManagement: boolean;

  /** Whether transfer-in is allowed */
  supportsTransfer: boolean;

  /** Post-expiry grace period in days */
  gracePeriodDays: number;

  /** Post-grace redemption period in days */
  redemptionPeriodDays: number;

  /** Whether the TLD is sellable */
  isEnabled: boolean;

  /** Whether the TLD is highlighted in search results */
  isFeatured: boolean;

  /** Display sort order (lower = first) */
  sortOrder: number;

  /** Creation timestamp (ISO 8601) */
  createdAt: string;

  /** Last update timestamp (ISO 8601, null if never updated) */
  updatedAt: string | null;
}

/**
 * Request body for creating a TLD catalog entry.
 * Mirrors backend CreateTldCatalogDto.
 */
export interface CreateTldCatalogRequest {
  tld: string;
  displayName: string;
  registrarProvider: string;
  registrarCost: number;
  registrationPrice: number;
  renewalPrice: number;
  transferPrice: number;
  restorePrice: number;
  currency: string;
  minRegistrationYears: number;
  maxRegistrationYears: number;
  supportsWhoisPrivacy: boolean;
  whoisPrivacyPrice: number;
  supportsDnsManagement: boolean;
  supportsTransfer: boolean;
  gracePeriodDays: number;
  redemptionPeriodDays: number;
  isEnabled: boolean;
  isFeatured: boolean;
  sortOrder: number;
}

/**
 * Request body for updating a TLD catalog entry.
 * Mirrors backend UpdateTldCatalogDto — `tld` is immutable so it is omitted.
 */
export type UpdateTldCatalogRequest = Omit<CreateTldCatalogRequest, "tld">;

/**
 * Request body for the enable/disable toggle endpoint.
 * Mirrors backend SetTldEnabledDto.
 */
export interface SetTldEnabledRequest {
  isEnabled: boolean;
}

// ============================================================================
// Domain Operations (P1 — actionable dashboard)
// ============================================================================

/**
 * One row in the operations dashboard — a domain registration that
 * needs admin attention (RegistrationFailed, stuck Registering,
 * TransferFailed, or stuck TransferPending). Mirrors backend
 * DomainOperationDto.
 *
 * NOTE: This is a flat DTO distinct from DomainRegistrationAdminDto.
 * The backend returns fields from the domain entity directly plus an
 * AttentionReason category field.
 */
export interface DomainOperationDto {
  /** Domain registration ID (UUID) */
  id: string;

  /** Owning tenant ID (UUID) */
  tenantId: string;

  /** Owning tenant display name */
  tenantName: string;

  /** Owning user ID (UUID) */
  userId: string;

  /** Owning user display name */
  userName: string;

  /** Fully-qualified domain name, e.g. "example.com" */
  fqdn: string;

  /** Registration lifecycle status */
  status: string;

  /** Order item ID that purchased this domain (UUID, null if none) */
  orderItemId: string | null;

  /** Registrar provider key, e.g. "ResellerClub" */
  registrarProvider: string;

  /** Registrar's domain/order reference, null when never registered */
  registrarReference: string | null;

  /** Failure reason from the registrar, null if none */
  failureReason: string | null;

  /**
   * Categorized reason this domain is on the dashboard. Known values:
   *   - "RegistrationFailed"     (P1)
   *   - "StuckRegistering"       (P1)
   *   - "TransferFailed"         (P1)
   *   - "StuckTransferPending"   (P2 — only present if the backend
   *                               surfaces TransferPending rows older
   *                               than the configured TransferStaleDays
   *                               threshold)
   *
   * Typed as `string` (not a union) so the frontend stays forward-
   * compatible if the backend adds further categories.
   */
  attentionReason: string;

  /** Creation timestamp (ISO 8601) */
  createdAt: string;

  /** Last update timestamp (ISO 8601) */
  updatedAt: string;

  /**
   * Number of DNS records configured for this domain.
   *
   * P2 backend addition — present only when the backend `GetDomainOperationsQuery`
   * eagerly counts DNS records for each row. When the backend does not include
   * the field, callers receive `undefined`; the dashboard cell renders "—".
   *
   * Note: This is a diagnostic field for the admin operations view only.
   * Admin DNS record CRUD is intentionally out of scope — DNS editing belongs
   * to the customer in the client app.
   */
  dnsRecordCount?: number | null;
}

/**
 * Filter parameters for the domain operations dashboard list.
 *
 * The backend does not support status filtering on this endpoint —
 * only pagination, a stuck-registering minutes threshold, and a
 * stuck-transfer days threshold are supported.
 */
export interface DomainOperationFilters {
  /** Page number (1-based) */
  page: number;

  /** Items per page */
  pageSize: number;

  /**
   * Minutes before a Registering domain is considered "stuck".
   * Defaults to 60 server-side when omitted.
   */
  stuckMinutes?: number;

  /**
   * Days before a TransferPending domain is considered "stale" and
   * surfaced on the dashboard. Defaults to the backend's
   * `TransferStaleDays` (typically 7) when omitted.
   *
   * P2 addition. If the backend ignores this parameter, the dashboard
   * simply does not filter by transfer staleness — same behaviour as P1.
   */
  stalePendingDays?: number;
}

/**
 * Result returned by an admin domain action (retry / reconcile / refund / cancel).
 * Mirrors backend DomainAdminActionResultDto.
 */
export interface DomainAdminActionResultDto {
  /** Domain registration ID acted on (UUID) */
  domainRegistrationId: string;

  /** Fully-qualified domain name */
  fqdn: string;

  /** Resulting lifecycle status as a string */
  status: string;

  /** Human-readable description of what the action did */
  message: string;

  /** Credit-note invoice ID; set only by a refund action */
  creditNoteInvoiceId: string | null;

  /** Refund payment ID; set only by a refund action */
  refundPaymentId: string | null;
}

/**
 * Request body for the manual reconcile endpoint.
 * Mirrors backend ManuallyReconcileDomainCommand.
 * `registrarReference` is the registrar's reference id;
 * `expiresAt` is a full ISO 8601 UTC datetime string — always include the
 * time component and the "Z" suffix (e.g. "2027-01-01T00:00:00Z") so the
 * backend DateTime parser receives an unambiguous UTC instant.
 * A bare YYYY-MM-DD value is NOT acceptable here.
 */
export interface ReconcileDomainRequest {
  /** Registrar's domain/order reference for the out-of-band registration */
  registrarReference: string;

  /**
   * Confirmed expiry as a full ISO 8601 UTC datetime string.
   * Must include the time component and the "Z" suffix,
   * e.g. "2027-01-01T00:00:00Z".
   */
  expiresAt: string;
}

/**
 * Request body for the partial-refund endpoint.
 * Mirrors backend RefundDomainItemCommand.
 */
export interface RefundDomainRequest {
  /** Admin-entered reason for the refund (audit trail) */
  reason: string;
}

/**
 * Request body for the cancel endpoint.
 * Mirrors backend CancelDomainRegistrationCommand.
 */
export interface CancelDomainRequest {
  /** Admin-entered reason for the cancellation (audit trail) */
  reason: string;
}

// ============================================================================
// Registrar Settings (P1 — read-only)
// ============================================================================

/**
 * Registrar connectivity status values emitted by the backend.
 * Mirrors backend RegistrarConnectivityStatus enum.
 */
export const REGISTRAR_CONNECTIVITY_STATUSES = [
  "Connected",
  "Unreachable",
  "Unknown",
] as const;

/** Union type derived from the connectivity status const array. */
export type RegistrarConnectivityStatus =
  (typeof REGISTRAR_CONNECTIVITY_STATUSES)[number];

/**
 * Active registrar provider configuration and connectivity.
 * Mirrors backend RegistrarSettingsDto.
 * Read-only for P1 — no secret editing. Secret values are never returned.
 */
export interface RegistrarSettingsDto {
  /** Configured active provider key (from RegistrarOptions), e.g. "ResellerClub" */
  configuredProvider: string;

  /** Provider key reported by the resolved registrar service */
  activeProvider: string;

  /** Platform default nameservers */
  defaultNameservers: string[];

  /**
   * Connectivity indicator: "Connected", "Unreachable", or "Unknown".
   * Uses the RegistrarConnectivityStatus union type.
   */
  connectivityStatus: RegistrarConnectivityStatus;
}

/**
 * Result of an on-demand registrar connectivity test.
 * Mirrors backend RegistrarConnectivityResultDto.
 */
export interface RegistrarConnectivityResultDto {
  /** Provider key tested */
  provider: string;

  /** Whether the registrar responded successfully */
  isReachable: boolean;

  /** Human-readable detail (probe target or error description) */
  detail: string;

  /** Round-trip latency in milliseconds */
  latencyMs: number;
}
